import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Save, Check, CircleCheck, CircleMinus, Lock, Edit2 } from 'lucide-react';
import Skeleton from '../common/Skeleton';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import { capaImpactCategoriesService, ImpactCategory } from '../../services/capaImpactCategoriesService';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import ApprovalDot from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';

const RBAC_ACTIVITY = 'CAP_MASTERS';

// workflow_state + audit fields come from the backend after the
// cap_impact_categories workflow migration.
type WorkflowFields = {
    workflow_state: 'draft' | 'submitted' | 'approved' | 'rejected';
};
type RowWithWorkflow = ImpactCategory & WorkflowFields;

const CapaImpactTypesPanel: React.FC = () => {
    // CAPA Impact Types master is activity-gated on CAP_MASTERS (maker-checker).
    const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
    const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
    const canView = viewPerm.allowed;
    const canModify = modifyPerm.allowed;
    const permLoading = viewPerm.loading;
    // Note: approve/reject/reopen gating is resolved by the WF engine inside
    // <WorkflowModal> (it returns per-action permissions), so no canApprove here.

    const [rows, setRows] = useState<RowWithWorkflow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('1');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState<RowWithWorkflow | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<{ impact_category_name: string; is_active: number }>({ impact_category_name: '', is_active: 1 });
    const fieldVh = useFieldValidation();
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [isFieldEditMode, setIsFieldEditMode] = useState(false);
    const [workflowOpenFor, setWorkflowOpenFor] = useState<RowWithWorkflow | null>(null);

    const load = useCallback(async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const data = await capaImpactCategoriesService.list();
            const sorted = [...data].sort((a, b) => b.id - a.id) as RowWithWorkflow[];
            setRows(sorted);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load impact categories') });
        } finally { setLoading(false); }
    }, [canView]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);
    useEffect(() => { setSubmitting(false); setToggling(false); setIsFieldEditMode(false); fieldVh.reset(); }, [selectedRow, showModal]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = rows.filter(r => {
        if (statusFilter !== 'ALL' && String(r.is_active ?? 0) !== statusFilter) return false;
        return r.impact_category_name.toLowerCase().includes(search.toLowerCase());
    });
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    const isApproved = selectedRow?.workflow_state === 'approved';
    const fieldsDisabled = editingId !== null && (!canModify || isApproved || !isFieldEditMode);

    const openCreate = () => {
        setSelectedRow(null);
        setEditingId(null);
        setForm({ impact_category_name: '', is_active: 1 });
        fieldVh.reset();
        setShowModal(true);
    };

    const openEdit = (r: RowWithWorkflow) => {
        setSelectedRow(r);
        setEditingId(r.id);
        setForm({ impact_category_name: r.impact_category_name, is_active: r.is_active });
        fieldVh.reset();
        setIsFieldEditMode(false); // open read-only — "Edit" unlocks the fields
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canModify) {
            setToast({ type: 'error', message: NOT_AUTHORISED_MSG });
            return;
        }
        const engineOk = fieldVh.validateAll([
            { name: 'impact_category_name', code: 109, value: form.impact_category_name, required: true },
        ]);
        const name = form.impact_category_name.trim();
        if (engineOk && !isUniqueLocal(956, name, rows, editingId ?? undefined)) {
            fieldVh.setError('impact_category_name', 'An impact type with this name already exists');
        }
        if (!engineOk || !isUniqueLocal(956, name, rows, editingId ?? undefined)) return;
        try {
            setSubmitting(true);
            if (editingId) {
                await capaImpactCategoriesService.update(editingId, { impact_category_name: form.impact_category_name });
                setToast({ type: 'success', message: 'Updated.' });
            } else {
                await capaImpactCategoriesService.create({ impact_category_name: form.impact_category_name });
                setToast({ type: 'success', message: 'Created.' });
            }
            setShowModal(false);
            load();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save') });
        } finally { setSubmitting(false); }
    };

    const handleToggleActive = async () => {
        if (!editingId || !canModify || isApproved) return;
        const next = form.is_active ? 0 : 1;
        const isActive = form.is_active;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this category?' : 'Inactivate this category?',
            text: next ? 'It will be selectable in impact assessment.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        try {
            setToggling(true);
            await capaImpactCategoriesService.update(editingId, { is_active: next });
            setForm(f => ({ ...f, is_active: next }));
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated.' : 'Inactivated.' });
            load();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to toggle') });
        } finally { setToggling(false); }
    };

    // Workflow (submit/approve/reject/reopen) is now driven end-to-end by the
    // unified <WorkflowModal> via the /api/v1/wf engine (kind="cap_impact_categories").

    // ── Not-authorised gate ───────────────────────────────────────────────────
    if (!permLoading && !canView) {
        return (
            <div className="netbody-card flex flex-col h-full">
                <NoDataFound title="Not authorised" message={viewPerm.message || NOT_AUTHORISED_MSG} />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <StatusSwitch
                    value={statusFilter === '0' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next === 'INACTIVE' ? '0' : '1')}
                    noun="impact types"
                    testId="SEL_capa_impact_status_filter"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        data-testid="INP_capa_impact_search"
                        type="text"
                        placeholder="Search impact types…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                    />
                    {canModify && (
                        <Button data-testid="BTN_capa_impact_create" variant="fill" icon={Plus} onClick={openCreate} disabled={permLoading}>Create</Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Name</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', '3xs', 'md']} rows={4} />}
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="table-list-cell">
                                                <NoDataFound title="No Impact Types Found" message='Click "Create" to add one.' />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((r, idx) => (
                                            <tr key={r.id} data-testid={`BTN_capa_impact_row_${r.id}_edit`} className={`table-list-row${r.is_active === 1 ? '' : ' is-inactive'}`} onClick={() => openEdit(r)} style={{ cursor: 'pointer' }}>
                                                <td
                                                    className="table-list-cell table-list-cell--dot"
                                                    onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor(r); }}
                                                    title="Workflow & history"
                                                    data-testid={`BTN_capa_impact_row_${r.id}_wf`}
                                                >
                                                    <ApprovalDot
                                                        state={(r.workflow_state || 'draft') as any}
                                                        onClick={e => { e.stopPropagation(); setWorkflowOpenFor(r); }}
                                                        title="Workflow & history"
                                                    />
                                                </td>
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    <span className="cell-code">
                                                        {(r as any).code || "—"}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title cell-name-lock" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {r.workflow_state === 'approved' && (
                                                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                        )}
                                                        <span>{r.impact_category_name}</span>
                                                    </span>
                                                </td>
                                            </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={filtered.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="impact types"
                        />
                    </div>
                </div>
            </div>

            <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md">
                <Modal.Header>
                    {editingId
                        ? <>{isApproved ? 'Viewing (locked)' : (canModify && isFieldEditMode) ? 'Editing' : 'Viewing'} "<span className="item-highlight">{form.impact_category_name}</span>"</>
                        : 'Create CAPA Impact Type'}
                </Modal.Header>
                <Modal.Body>
                    {isApproved && (
                        <div className="approved-banner">
                            <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                        </div>
                    )}
                    <form id="cap-impact-type-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
                        {/* System code — read-only, prefilled with the same code the list shows. */}
                        {selectedRow && (
                            <FormControl.Input
                                label="Code"
                                value={(selectedRow as any)?.code || '—'}
                                disabled
                                data-testid="INP_capa_impact_code"
                            />
                        )}
                        <ValidatedInput
                            vh={fieldVh}
                            name="impact_category_name"
                            code={109}
                            required
                            data-testid="INP_capa_impact_form_name"
                            label="Name"
                            disabled={fieldsDisabled}
                            value={form.impact_category_name}
                            onChange={e => setForm(f => ({ ...f, impact_category_name: e.target.value }))}
                            placeholder="e.g. Reputational, Regulatory, Operational"
                            maxLength={100}
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer className={editingId ? 'app-modal-footer--split' : ''}>
                    {editingId && canModify && !isApproved && (
                        <Button
                            data-testid="BTN_capa_impact_form_toggle_status"
                            variant={form.is_active ? 'inactive-outline' : 'active-outline'}
                            icon={form.is_active ? CircleMinus : CircleCheck}
                            onClick={handleToggleActive}
                            disabled={toggling || submitting}
                        >
                            {toggling ? '...' : form.is_active ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    {!editingId && (
                        <Button
                            data-testid="BTN_capa_impact_form_submit"
                            variant="fill"
                            icon={Save}
                            onClick={handleSubmit}
                            disabled={submitting || permLoading || !canModify}
                        >
                            {submitting ? 'Submitting…' : 'Submit'}
                        </Button>
                    )}
                    {editingId && canModify && !isApproved && (
                        isFieldEditMode ? (
                            <Button
                                data-testid="BTN_capa_impact_form_update"
                                variant="update"
                                icon={Check}
                                onClick={handleSubmit}
                                disabled={submitting || toggling || permLoading}
                            >
                                {submitting ? 'Updating…' : 'Update'}
                            </Button>
                        ) : (
                            <Button
                                data-testid="BTN_capa_impact_form_edit"
                                variant="fill"
                                icon={Edit2}
                                onClick={() => setIsFieldEditMode(true)}
                                disabled={permLoading || toggling}
                            >
                                Edit
                            </Button>
                        )
                    )}
                </Modal.Footer>
            </Modal>

            <WorkflowModal
                isOpen={!!workflowOpenFor}
                onClose={() => setWorkflowOpenFor(null)}
                kind="cap_impact_categories"
                id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
                onChanged={load}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default CapaImpactTypesPanel;
